import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logout, getCurrentUserId, setAdhaarVerified } from "../../utils/auth";
import { toast } from "react-toastify";
import AadhaarStatus from "../../components/AadhaarStatus";
import "../../css/User/Aadhaar.css";
import { getSidebarMenus } from "../../constants/sidebarMenus";
import { useProfile } from "../../hook/useProfile";
import { documentsApi } from "../../api/documentsApi";
import { digioKycApi } from "../../api/digioKycApi";
import { useDigio } from "../../hooks/useDigio";
import api from "../../api/axios";
import TopHeader from "../../components/comman/TopHeader";
import Sidebar from "../../components/comman/Sidebar";
import { Icon } from "@iconify/react";

const DOCUMENT_TYPES = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "pan", label: "PAN Card" },
  { value: "voter", label: "Voter ID" },
  { value: "driving", label: "Driving Licence" },
];

const DOC_CONFIG = {
  aadhaar: {
    title: "Aadhaar Verification",
    numberLabel: "Aadhaar Number",
    placeholder: "Enter 12-digit Aadhaar number",
    hasFront: true,
    hasBack: true,
  },
  pan: {
    title: "PAN Verification",
    numberLabel: "PAN Number",
    placeholder: "Enter PAN Number",
    hasFront: false,
    hasBack: false,
    hasSingle: true,
  },
  voter: {
    title: "Voter ID Verification",
    numberLabel: "Voter ID Number",
    placeholder: "Enter Voter ID",
    hasFront: true,
    hasBack: true,
  },
  driving: {
    title: "Driving Licence Verification",
    numberLabel: "Licence Number",
    placeholder: "Enter DL Number",
    hasFront: true,
    hasBack: true,
  },
};

function AadhaarVerification() {
  const currentUserId = getCurrentUserId();
  const { isProfileInfo, setIsProfileInfo } = useProfile();
  const { startDigioKyc, loading: digioLoading, error: digioError } = useDigio();
  const [showMsgDrawer, setShowMsgDrawer] = useState(false);
  const feedTYPE = window.location.pathname.includes("/feed/public") ? "public" : "prime";
  const [feedType, setFeedType] = useState(feedTYPE);
  const [localAadhaarStatus, setLocalAadhaarStatus] = useState(null);
  const [localVerifiedBy, setLocalVerifiedBy] = useState(null);
  const [digioInitLoading, setDigioInitLoading] = useState(false);
  const navigate = useNavigate();
  const [activeSidebar, setActiveSidebar] = useState("aadhaar");
  const [showSidebar, setShowSidebar] = useState(true);
  const sidebarMenus = getSidebarMenus(currentUserId);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const defaultDoc = DOCUMENT_TYPES.find(doc => doc.value === "aadhaar");
  const [selectedDoc, setSelectedDoc] = useState(defaultDoc);

  const [form, setForm] = useState({
    docNumber: "",
    frontImage: null,
    backImage: null,
    singleImage: null,
  });

  const MAX_FILE_SIZE = 8 * 1024 * 1024;

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleDocSelect = (doc) => {
    setSelectedDoc(doc);
    setDropdownOpen(false);
    setForm({ docNumber: "", frontImage: null, backImage: null, singleImage: null });
    setErrors({});
  };

  const refreshProfile = useCallback(async () => {
    try {
      const response = await api.get("/user/me");
      setIsProfileInfo(response.data.user);
      if (response.data.user?.adhaarVerified) {
        setAdhaarVerified(response.data.user.adhaarVerified);
      }
      if (response.data.user?.adhaarVerifiedBy) {
        setLocalVerifiedBy(response.data.user.adhaarVerifiedBy);
      }
    } catch {
      /* best-effort */
    }
  }, [setIsProfileInfo]);

  const pollDigioStatus = useCallback(async () => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const statusRes = await digioKycApi.getKycStatus();
        const status = statusRes?.adhaarVerified;
        if (status === "VERIFIED" || status === "REJECTED") {
          setLocalAadhaarStatus(status);
          setLocalVerifiedBy(statusRes?.adhaarVerifiedBy || null);
          await refreshProfile();
          return status;
        }
      } catch {
        /* keep polling — webhook may still be in flight */
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    await refreshProfile();
    return null;
  }, [refreshProfile]);

  const handleVerifyWithDigio = async () => {
    setDigioInitLoading(true);
    try {
      const initRes = await digioKycApi.initKyc();
      if (!initRes?.kycId || !initRes?.customerIdentifier) {
        throw new Error(initRes?.error || "Could not start DigiLocker verification");
      }

      const sdkResult = await startDigioKyc({
        kycId: initRes.kycId,
        customerIdentifier: initRes.customerIdentifier,
        accessTokenId: initRes.accessTokenId,
        accessToken: initRes.accessToken,
        isSandbox: initRes.isSandbox !== false,
      });

      if (sdkResult?.cancelled) {
        return;
      }

      try {
        await digioKycApi.syncKyc();
      } catch {
        /* sync is best-effort; polling continues below */
      }

      const finalStatus = await pollDigioStatus();
      if (finalStatus === "VERIFIED") {
        toast.success("Verified via DigiLocker");
      } else if (finalStatus === "REJECTED") {
        toast.error(
          "DigiLocker verification failed. You can try again or upload manually.",
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        digioError ||
        "Failed to start DigiLocker verification";
      toast.error(msg);
    } finally {
      setDigioInitLoading(false);
    }
  };

  useEffect(() => {
    if (defaultDoc) {
      handleDocSelect(defaultDoc);
    }
  }, []);
  
  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size should not exceed 8MB");
      return;
    }
    setForm((prev) => ({ ...prev, [type]: file }));
    setErrors((prev) => ({ ...prev, [type]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    const config = DOC_CONFIG[selectedDoc?.value];

    if (!form.docNumber.trim()) {
      newErrors.docNumber = `Please enter ${config.numberLabel}`;
    }

    if (selectedDoc?.value === "aadhaar" && !/^\d{12}$/.test(form.docNumber)) {
      newErrors.docNumber = "Aadhaar number must be exactly 12 digits";
    }

    if (config.hasFront && !form.frontImage) {
      newErrors.frontImage = "Please upload front image";
    }
    if (config.hasBack && !form.backImage) {
      newErrors.backImage = "Please upload back image";
    }
    if (config.hasSingle && !form.singleImage) {
      newErrors.singleImage = "Please upload document photo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async () => {
    if (!selectedDoc) {
      toast.error("Please select a document type");
      return;
    }
    if (!validate()) {
      toast.error("Please fill all details correctly");
      return;
    }

    setLoading(true);
    try {
      if (selectedDoc.value === "aadhaar") {
        await documentsApi.uploadAadhaarManual({
          aadhaarNumber: form.docNumber,
          frontImage: form.frontImage,
          backImage: form.backImage,
        });
        toast.success("Aadhaar submitted successfully...");
        setLocalAadhaarStatus("REQUESTED");
        setLocalVerifiedBy("MANUAL");
        setAdhaarVerified("REQUESTED");
        await refreshProfile();
        setLoading(false);
        return;
      }

      const formData = new FormData();

      // documentType — backend expects this exact field name
      const docTypeMap = {
        aadhaar: "AADHAAR",
        pan: "PAN",
        voter: "VOTER_ID",
        driving: "DRIVING_LICENCE",
      };

      formData.append("documentType", docTypeMap[selectedDoc.value]);

      // documentNumber — backend expects this exact field name
      formData.append("documentNumber", form.docNumber);

      // Images
      if (form.frontImage) formData.append("frontImage", form.frontImage);
      if (form.backImage) formData.append("backImage", form.backImage);
      if (form.singleImage) formData.append("frontImage", form.singleImage); // PAN single image goes as frontImage

      await documentsApi.uploadAadhaar(formData);
      toast.success("Document submitted successfully!");
      setLoading(false);
      setLocalAadhaarStatus("REQUESTED");
    } catch (err) {
      const errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Submission failed. Try again.";
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isProfileInfo?.adhaarVerified) {
      setLocalAadhaarStatus(isProfileInfo.adhaarVerified);
    }
    if (isProfileInfo?.adhaarVerifiedBy) {
      setLocalVerifiedBy(isProfileInfo.adhaarVerifiedBy);
    }
  }, [isProfileInfo?.adhaarVerified, isProfileInfo?.adhaarVerifiedBy]);

  const effectiveAadhaarStatus =
    localAadhaarStatus !== null ? localAadhaarStatus : isProfileInfo?.adhaarVerified;

  const effectiveVerifiedBy =
    localVerifiedBy !== null ? localVerifiedBy : isProfileInfo?.adhaarVerifiedBy;

  const isBusy = loading || digioInitLoading || digioLoading;
  const isDigioBusy = digioInitLoading || digioLoading;

  const showStatus =
    effectiveAadhaarStatus === "VERIFIED" ||
    effectiveAadhaarStatus === "REJECTED" ||
    (effectiveAadhaarStatus === "REQUESTED" && effectiveVerifiedBy === "MANUAL");

  const showOptions =
    effectiveAadhaarStatus !== "VERIFIED" &&
    !(effectiveAadhaarStatus === "REQUESTED" && effectiveVerifiedBy === "MANUAL");

  const config = selectedDoc ? DOC_CONFIG[selectedDoc.value] : null;

  // Upload box component
  const UploadBox = ({ type, label }) => {
    const file = form[type];
    return (
      <div
        className="kyc-upload-box"
        onClick={() => document.getElementById(`file-${type}`).click()}
      >
        <input
          type="file"
          id={`file-${type}`}
          accept="image/*"
          hidden
          onChange={(e) => handleFileChange(e, type)}
        />
        {file ? (
          <img
            src={URL.createObjectURL(file)}
            alt={label}
            className="kyc-upload-preview"
          />
        ) : (
          <>
            <Icon icon="mdi:camera-plus-outline" className="kyc-camera-icon" />
            <p className="kyc-upload-title">{label}</p>
            <p className="kyc-upload-subtitle">Tap to capture or select from gallery</p>
            <button className="kyc-select-btn" type="button">Select File</button>
          </>
        )}
        {errors[type] && <p className="kyc-error">{errors[type]}</p>}
      </div>
    );
  };

  return (
    <div className="container-xxl p-0">
      <div className="feed-page-wrapper">
        <TopHeader
          onToggleSidebar={() => setShowSidebar((prev) => !prev)}
          onOpenMessages={() => setShowMsgDrawer(true)}
          creditAmount={50}
          notificationCount={5}
          messageCount={0}
        />
        <div className="prime-layout-container .container-fluid">
          <div className="row">
            {!showSidebar && (
              <div className="left-sidebar-overlay d-md-none" onClick={() => setShowSidebar(true)} />
            )}
            <Sidebar
              showSidebar={showSidebar}
              sidebarMenus={sidebarMenus}
              activeSidebar={activeSidebar}
              feedType={feedType}
              onNavigate={(item) => {
                setActiveSidebar(item.key);
                if (item.path) navigate(item.path);
              }}
              onLogout={handleLogout}
            />

            <div className="col-lg-9 col-md-9 col-12 overflow-auto center-area-container">
              <div className="kyc-page-wrapper">

                {showStatus && (
                  <AadhaarStatus
                    adhaarVerified={effectiveAadhaarStatus}
                    adhaarVerifiedBy={effectiveVerifiedBy}
                  />
                )}

                {showOptions && (
                  <>
                    <h2 className="kyc-page-title">Upload Your Documents</h2>

                    <div className="kyc-dropdown-wrapper">
                      <div
                        className="kyc-dropdown-trigger"
                        onClick={() => setDropdownOpen((prev) => !prev)}
                      >
                        <span>{selectedDoc ? selectedDoc.label : "Choose Your Documents"}</span>
                        <Icon icon="mdi:chevron-down" className={`kyc-chevron ${dropdownOpen ? "open" : ""}`} />
                      </div>

                      {dropdownOpen && (
                        <div className="kyc-dropdown-menu">
                          {DOCUMENT_TYPES.map((doc) => (
                            <div
                              key={doc.value}
                              className={`kyc-dropdown-item ${selectedDoc?.value === doc.value ? "active" : ""}`}
                              onClick={() => handleDocSelect(doc)}
                            >
                              {doc.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {selectedDoc && config && (
                      <div className="kyc-form-section">

                        {selectedDoc.value === "aadhaar" && (
                          <div className="aadhaar-verify-options">
                            <DigioVerifyForm
                              onVerify={handleVerifyWithDigio}
                              isBusy={isDigioBusy}
                            />
                            <p className="aadhaar-verify-divider">— OR —</p>
                          </div>
                        )}

                        <p className="kyc-field-label">{config.numberLabel}</p>
                        <div className="kyc-input-wrapper">
                          <Icon icon="mdi:credit-card-outline" className="kyc-input-icon" />
                          <input
                            type="text"
                            className="kyc-input"
                            placeholder={config.placeholder}
                            value={form.docNumber}
                            maxLength={selectedDoc.value === "aadhaar" ? 12 : 20}
                            onChange={(e) => {
                              const val = selectedDoc.value === "aadhaar"
                                ? e.target.value.replace(/\D/g, "")
                                : e.target.value.toUpperCase();
                              setForm((prev) => ({ ...prev, docNumber: val }));
                              setErrors((prev) => ({ ...prev, docNumber: "" }));
                            }}
                          />
                        </div>
                        {errors.docNumber && <p className="kyc-error">{errors.docNumber}</p>}

                        <p className="kyc-upload-hint">
                          Please upload clear images of both the front and back sides of your {selectedDoc.label} for secure identity verification
                        </p>

                        {/* UPLOAD BOXES */}
                        {config.hasSingle && (
                          <UploadBox type="singleImage" label="Document Photo" />
                        )}

                        {config.hasFront && config.hasBack && (
                          <div className="kyc-upload-row">
                            <UploadBox type="frontImage" label="Front Side" />
                            <UploadBox type="backImage" label="Back Side" />
                          </div>
                        )}

                        {/* SUBMIT BUTTON */}
                        <button
                          className="kyc-submit-btn"
                          onClick={handleSubmit}
                          disabled={isBusy}
                        >
                          {loading ? "Submitting..." : "Submit for Verification"}
                        </button>

                        <p className="kyc-secure-text">
                          <Icon icon="mdi:lock-outline" className="me-1" />
                          Your Data Is Encrypted And Secure
                        </p>

                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DigioVerifyForm({ onVerify, isBusy }) {
  return (
    <div className="aadhaar-digio-card">
      <h3>Verify with DigiLocker</h3>
      <p className="text-muted mb-3">
        Instant verification via Digio and DigiLocker — no manual review needed.
      </p>
      <button
        type="button"
        className="kyc-digio-btn"
        onClick={onVerify}
        disabled={isBusy}
      >
        {isBusy ? "Please wait..." : "Verify with DigiLocker"}
      </button>
    </div>
  );
}

export default AadhaarVerification;


// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { FaArrowLeft, FaTimes, FaCheck } from "react-icons/fa";
// import { logout, getCurrentUserId } from "../../utils/auth";
// import { toast } from "react-toastify";
// import AadhaarStatus from "../../components/AadhaarStatus";
// // import "../../css/User/Kyc.css";
// import "../../css/User/Aadhaar.css";
// import { getSidebarMenus } from "../../constants/sidebarMenus";
// import { useProfile } from "../../hook/useProfile";
// import { documentsApi } from "../../api/documentsApi";
// import TopHeader from "../../components/comman/TopHeader";
// import Sidebar from "../../components/comman/Sidebar";
// import Input from "../../components/comman/Input";

// function Kyc3() {
//   const currentUserId = getCurrentUserId();
//   const BASE_URL = process.env.REACT_APP_BASE_URL;
//   const { isProfileInfo, isLoading } = useProfile();
//   const [showMsgDrawer, setShowMsgDrawer] = useState(false);
//   const feedTYPE = window.location.pathname.includes("/feed/public")
//     ? "public"
//     : "prime";
//   const [feedType, setFeedType] = useState(feedTYPE);
//   const [localAadhaarStatus, setLocalAadhaarStatus] = useState(null);
//   const navigate = useNavigate();
//   const [activeSidebar, setActiveSidebar] = useState("aadhaar");
//   const [showSidebar, setShowSidebar] = useState(true);
//   const sidebarMenus = getSidebarMenus(currentUserId);

//   // handle logout
//   const handleLogout = () => {
//     logout();
//     navigate("/login", { replace: true });
//   };

//   const [form, setForm] = useState({
//     aadhaarNumber: "",
//     frontImage: null,
//     backImage: null,
//   });

//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   // const [statusRequested, setStatusRequested] = useState("");

//   const MAX_FILE_SIZE = 8 * 1024 * 1024;

//   const handleFileChange = (e, type) => {
//     const file = e.target.files[0];

//     if (!file) return;

//     // Check file type (optional but recommended)
//     if (!file.type.startsWith("image/")) {
//       toast.error("Only image files are allowed");
//       return;
//     }

//     // Check file size (10MB max)
//     if (file.size > MAX_FILE_SIZE) {
//       toast.error("File size should not exceed 10MB");
//       return;
//     }

//     // Set file
//     setForm((prev) => ({
//       ...prev,
//       [type]: file,
//     }));

//     // Clear error if valid
//     setErrors((prev) => ({
//       ...prev,
//       [type]: "",
//     }));
//   };

//   // ========= FORM SUBMISION  ========= //
//   const handleSubmit = async () => {
//     if (!validate()) {
//       toast.error("Please fill all Aadhaar details correctly");
//       return;
//     }

//     setLoading(true);

//     try {
//       await documentsApi.uploadAadhaar(form);

//       toast.success("Aadhaar submitted successfully...");

//       // STOP LOADING FIRST
//       setLoading(false);

//       // FORCE STATUS SWITCH
//       setLocalAadhaarStatus("REQUESTED");
//     } catch (err) {
//       const errorMsg =
//         err?.response?.data?.error ||
//         err?.response?.data?.message ||
//         "Aadhaar verification failed";

//       toast.error(errorMsg);
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (isProfileInfo?.adhaarVerified === "REQUESTED") {
//       setLocalAadhaarStatus("REQUESTED");
//     }
//   }, [isProfileInfo]);

//   const validate = () => {
//     const newErrors = {};

//     if (!/^\d{12}$/.test(form.aadhaarNumber)) {
//       newErrors.aadhaarNumber = "Aadhaar number must be 12 digits";
//     }

//     if (!form.frontImage) {
//       newErrors.frontImage = "Please upload Aadhaar front image";
//     }

//     if (!form.backImage) {
//       newErrors.backImage = "Please upload Aadhaar back image";
//     }

//     if (form.frontImage && form.frontImage.size > MAX_FILE_SIZE) {
//       newErrors.frontImage = "Front image must be less than 10MB";
//     }

//     if (form.backImage && form.backImage.size > MAX_FILE_SIZE) {
//       newErrors.backImage = "Back image must be less than 10MB";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const effectiveAadhaarStatus =
//     localAadhaarStatus !== null
//       ? localAadhaarStatus
//       : isProfileInfo?.adhaarVerified;

//   return (
//     <div className="container-xxl p-0">
//       <div className="feed-page-wrapper">
//         <TopHeader
//           onToggleSidebar={() => setShowSidebar((prev) => !prev)}
//           onOpenMessages={() => setShowMsgDrawer(true)}
//           creditAmount={50}
//           notificationCount={5}
//           messageCount={0}
//         />
//         <div className="prime-layout-container .container-fluid">
//           <div className="row">
//             {!showSidebar && (
//               <div
//                 className="left-sidebar-overlay d-md-none"
//                 onClick={() => setShowSidebar(true)}
//               />
//             )}

//             <Sidebar
//               showSidebar={showSidebar}
//               sidebarMenus={sidebarMenus}
//               activeSidebar={activeSidebar}
//               feedType={feedType}
//               onNavigate={(item) => {
//                 setActiveSidebar(item.key);
//                 if (item.path) {
//                   navigate(item.path);
//                 }
//               }}
//               onLogout={handleLogout}
//             />

//             <div className="col-lg-9 col-md-9 col-12 overflow-auto center-area-container">
//               {/* ================= KYC PAGE START ================= */}
//               <div className="Aadhar_page_container col-12">
//                 <div className="Aadhar_page">
//                   {/* <div className="kyc-card col-12 col-sm-11 col-md-9"> */}
//                   <div className="Aadhar_card ">
//                     <>
//                       {(effectiveAadhaarStatus === "VERIFIED" ||
//                         effectiveAadhaarStatus === "REQUESTED") && (
//                           <AadhaarStatus
//                             adhaarVerified={effectiveAadhaarStatus}
//                           />
//                         )}

//                       {(effectiveAadhaarStatus === "REJECTED" ||
//                         effectiveAadhaarStatus === "NONE") && (
//                           <div className="Aadhar-form shadow">
//                             <h3 className="theme-color">Aadhaar Details</h3>
//                             <Input
//                               name="aadhaarNumber"
//                               placeholder="Enter 12-digit Aadhaar number"
//                               className="password-input"
//                               value={form.aadhaarNumber}
//                               maxLength={12}
//                               onChange={(e) =>
//                                 setForm((prev) => ({
//                                   ...prev,
//                                   aadhaarNumber: e.target.value.replace(
//                                     /\D/g,
//                                     "",
//                                   ),
//                                 }))
//                               }
//                             />
//                             {errors.aadhaarNumber && (
//                               <p className="error">{errors.aadhaarNumber}</p>
//                             )}

//                             <div className="row col-md-gap-3 mt-3">
//                               <div className="col-12 col-md-6">
//                                 <div
//                                   className="aadhaar_upload_box"
//                                   onClick={() =>
//                                     document.getElementById("frontImage").click()
//                                   }
//                                 >
//                                   {form.frontImage ? (
//                                     <img
//                                       src={URL.createObjectURL(form.frontImage)}
//                                       alt="Aadhaar Front"
//                                     />
//                                   ) : (
//                                     <span>Upload Aadhaar Front</span>
//                                   )}
//                                   <input
//                                     type="file"
//                                     id="frontImage"
//                                     accept="image/*"
//                                     hidden
//                                     onChange={(e) => {
//                                       handleFileChange(e, "frontImage")
//                                       setForm((prev) => ({
//                                         ...prev,
//                                         frontImage: e.target.files[0],
//                                       }))
//                                     }
//                                     }
//                                   />
//                                 </div>
//                                 {errors.frontImage && (
//                                   <p className="error mt-1">
//                                     {errors.frontImage}
//                                   </p>
//                                 )}
//                               </div>

//                               <div className="col-12 mt-3 m-md-0 col-md-6">
//                                 <div
//                                   className="aadhaar_upload_box"
//                                   onClick={() =>
//                                     document.getElementById("backImage").click()
//                                   }
//                                 >
//                                   {form.backImage ? (
//                                     <img
//                                       src={URL.createObjectURL(form.backImage)}
//                                       alt="Aadhaar Back"
//                                     />
//                                   ) : (
//                                     <span>Upload Aadhaar Back</span>
//                                   )}
//                                   <input
//                                     type="file"
//                                     id="backImage"
//                                     accept="image/*"
//                                     hidden
//                                     onChange={(e) => {
//                                       handleFileChange(e, "backImage")
//                                       setForm((prev) => ({
//                                         ...prev,
//                                         backImage: e.target.files[0],
//                                       }))
//                                     }

//                                     }
//                                   />
//                                 </div>
//                                 {errors.backImage && (
//                                   <p className="error mt-1">{errors.backImage}</p>
//                                 )}
//                               </div>
//                             </div>

//                             <div className="d-flex justify-content-center mt-3">
//                               <button
//                                 className="continue01-btn"
//                                 onClick={handleSubmit}
//                                 disabled={loading}
//                               >
//                                 {loading ? "Submitting..." : "Submit"}
//                               </button>
//                             </div>
//                           </div>
//                         )}
//                     </>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }
// export default Kyc3;
